import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, StickyNote, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtBytes } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MAX_TOTAL = 25 * 1024 * 1024;

/** mode = "reply" (emails the requester) or "note" (internal). Starts collapsed so the newest message stays in view. */
export default function Composer({ t, onChanged, mode = "reply" }) {
  const { can, readOnly } = useAuth();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const isNote = mode === "note";
  if (readOnly || (isNote ? !can("tickets.note") : !can("tickets.reply"))) return null;
  const total = pending.reduce((a, f) => a + f.size, 0);

  const addFiles = (files) => {
    const next = [...pending, ...Array.from(files || [])];
    if (next.reduce((a, f) => a + f.size, 0) > MAX_TOTAL) { toast.error("Attachments are limited to 25 MB per message"); return; }
    setPending(next);
    setOpen(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!body.trim() && !pending.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", mode);
      fd.append("body", body);
      pending.forEach((f) => fd.append("files", f));
      await api.post(`/tickets/${t.id}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const files = pending.length ? ` with ${pending.length} attachment(s)` : "";
      toast.success(isNote ? `Note added${files}` : `Reply emailed to ${t.from_email}${files}`);
      setBody("");
      setPending([]);
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={`surface flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:text-foreground ${isNote ? "border-amber-500/50" : "border-blue-500/50"}`} data-testid={`composer-open-${mode}`}>
        {isNote ? <StickyNote className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
        {isNote ? "Add an internal note — only your team sees it" : `Write a reply — emailed to ${t.from_email}`}
      </button>
    );
  }

  return (
    <div className={`surface border-l-2 ${isNote ? "border-amber-500/50" : "border-blue-500/50"}`} data-testid="composer" data-mode={mode}>
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5 text-xs font-medium">
        {isNote ? <span className="flex items-center gap-1 px-2 text-amber-600 dark:text-amber-300"><StickyNote className="h-3.5 w-3.5" /> Internal note</span>
          : <span className="flex items-center gap-1 px-2 text-blue-600 dark:text-blue-300"><Send className="h-3.5 w-3.5" /> Email {t.from_email}</span>}
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="composer-file-input" />
        <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="composer-attach-btn"><Paperclip className="mr-1 h-3.5 w-3.5" /> Attach files</Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)} disabled={busy} data-testid="composer-collapse-btn"><X className="h-3.5 w-3.5" /></Button>
      </div>
      <Textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder={isNote ? "Write an internal note — only your team sees this." : `Write a reply. It is emailed to ${t.from_email} with the ticket reference.`} className="rounded-none border-0 focus-visible:ring-0" data-testid="composer-body-input" />
      {pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-3 py-2" data-testid="composer-pending-files">
          {pending.map((f, i) => (
            <span key={`${f.name}-${i}`} className="mono inline-flex items-center gap-1 border border-border bg-muted/40 px-2 py-0.5 text-[11px]" data-testid={`composer-pending-${i}`}>
              <Paperclip className="h-3 w-3" /> {f.name} <span className="text-muted-foreground">{fmtBytes(f.size)}</span>
              <button type="button" onClick={() => setPending(pending.filter((_, j) => j !== i))} className="ml-1 text-muted-foreground hover:text-foreground" aria-label={`Remove ${f.name}`} data-testid={`composer-remove-${i}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
          <span className="ml-auto text-[11px] text-muted-foreground">{isNote ? "attached to the ticket" : "sent with the email"} · {fmtBytes(total)}</span>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-border px-3 py-2">
        <span className="text-[11px] text-muted-foreground">{isNote ? "Not visible to the requester" : "Sent from Total Tickets via email"}</span>
        <Button size="sm" className="h-7" disabled={busy || (!body.trim() && !pending.length)} onClick={send} data-testid="composer-send-btn">{busy ? "Sending…" : isNote ? "Add note" : `Send reply${pending.length ? ` + ${pending.length} file(s)` : ""}`}</Button>
      </div>
    </div>
  );
}
